<template>
  <v-container class="impressum-container">
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">mdi-information</v-icon>
        Impressum
        <v-spacer></v-spacer>
        <v-chip color="success" size="small">Stand: 22. März 2025</v-chip>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-6">
          Diese Seite enthält die rechtlichen Informationen zum Betreiber dieser
          Anwendung.
        </p>

        <div class="status-alert mb-6">
          <v-icon color="success" size="20" class="mr-2"
            >mdi-information</v-icon
          >
          <div>Diese Website wird privat betrieben.</div>
        </div>

        <!-- Angaben gemäß § 5 TMG -->
        <h2 class="section-header">
          <v-icon color="primary" class="mr-2">mdi-account-tie</v-icon>
          Angaben gemäß § 5 TMG
        </h2>

        <h3 class="subsection-title mt-4">
          Verantwortlicher im Sinne des TMG:
        </h3>

        <v-card class="contact-card" color="primary-lighten-5">
          <v-card-text class="py-3 px-4">
            <div class="d-flex mb-2">
              <div class="text-subtitle-2 mr-2 min-width-100">Name:</div>
              <div>{{ ownerName }}</div>
            </div>
            <div class="d-flex mb-2">
              <div class="text-subtitle-2 mr-2 min-width-100">Adresse:</div>
              <div>{{ address }}</div>
            </div>
            <div class="d-flex mb-2">
              <div class="text-subtitle-2 mr-2 min-width-100">Telefon:</div>
              <div>{{ phone }}</div>
            </div>
            <div class="d-flex">
              <div class="text-subtitle-2 mr-2 min-width-100">E-Mail:</div>
              <a
                :href="emailLink"
                v-html="obfuscatedEmail"
                class="email-link"
              ></a>
            </div>
          </v-card-text>
        </v-card>

        <!-- Haftungsausschluss -->
        <h2 class="section-header mt-6">
          <v-icon color="primary" class="mr-2">mdi-scale-balance</v-icon>
          Haftungsausschluss
        </h2>

        <v-row class="mt-3">
          <v-col cols="12" md="6">
            <v-card class="section-card" color="info-lighten-5">
              <v-card-title class="py-2 px-4 d-flex align-center">
                <v-icon color="success" class="mr-2"
                  >mdi-file-document-outline</v-icon
                >
                Haftung für Inhalte
              </v-card-title>

              <v-card-text class="py-2 px-4">
                <p class="section-text">
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene
                  Inhalte auf diesen Seiten nach den allgemeinen Gesetzen
                  verantwortlich. Nach §§ 8 bis 10 TMG sind wir jedoch nicht
                  verpflichtet, übermittelte oder gespeicherte fremde
                  Informationen zu überwachen oder nach Umständen zu forschen,
                  die auf eine rechtswidrige Tätigkeit hinweisen.
                </p>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="section-card" color="info-lighten-5">
              <v-card-title class="py-2 px-4 d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-link-variant</v-icon>
                Haftung für Links
              </v-card-title>

              <v-card-text class="py-2 px-4">
                <p class="section-text">
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf
                  deren Inhalte wir keinen Einfluss haben. Deshalb können wir
                  für diese fremden Inhalte auch keine Gewähr übernehmen.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Urheberrecht und weitere Hinweise -->
        <h2 class="section-header mt-5">
          <v-icon color="primary" class="mr-2">mdi-copyright</v-icon>
          Urheberrecht
        </h2>

        <p class="section-text mt-3">
          Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen
          Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung,
          Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
          Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des
          jeweiligen Autors bzw. Erstellers.
        </p>

        <h2 class="section-header mt-5">
          <v-icon color="primary" class="mr-2">mdi-note-text</v-icon>
          Hinweis gemäß § 55 Abs. 2 RStV
        </h2>

        <p class="section-text mt-3">
          Gemäß § 55 Abs. 2 RStV weisen wir darauf hin, dass alle Informationen
          auf dieser Website mit größtmöglicher Sorgfalt erstellt werden.
          Dennoch übernimmt der Anbieter keine Gewähr für die Aktualität,
          Richtigkeit und Vollständigkeit der bereitgestellten Inhalte.
        </p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed } from "vue";

const ownerName = computed(() => "Andreas Reschke");
const address = computed(() => "Merscheider Str. 15, 42699 Solingen");
const phone = computed(() => "+49 176 72242015");

// Definition der E-Mail-Bestandteile:
const emailUser = "info";
const emailDomain = "alphadystopie";
const emailTld = "de";

// Zusammensetzen der E-Mail-Adresse:
const email = emailUser + "@" + emailDomain + "." + emailTld;

// Erzeugung des klickbaren Mailto-Links:
const emailLink = computed(() => `mailto:${email}`);

// Funktion zur Umwandlung in HTML-Entities:
function toHtmlEntities(str) {
  return str
    .split("")
    .map((char) => "&#" + char.charCodeAt(0) + ";")
    .join("");
}

// Erzeugung der obfuskierten E-Mail-Adresse:
const obfuscatedEmail = computed(() => toHtmlEntities(email));
</script>

<style scoped>
.impressum-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.section-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.min-width-100 {
  min-width: 100px;
}

.contact-card {
  border-radius: 10px;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 24px;
}

.contact-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.section-header {
  font-size: 1.25rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
}

.subsection-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
}

.section-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  line-height: 1.6;
}

.status-alert {
  display: flex;
  align-items: center;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.email-link {
  color: rgba(76, 175, 80, 0.9);
  text-decoration: none;
  transition: color 0.2s ease;
}

.email-link:hover {
  color: rgba(76, 175, 80, 1);
  text-decoration: underline;
}

/* Responsive Anpassungen */
@media (max-width: 600px) {
  .section-header {
    font-size: 1.1rem;
  }

  .subsection-title {
    font-size: 1rem;
  }

  .section-text {
    font-size: 0.9rem;
  }
}
</style>
